// pages/InspectionDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import InspectionType from './InspectionType';
import DevicesTested from './DevicesTested';
import TechNotes from './TechNotes';
import AdditionalTech from './AdditionalTech';
import LogBooks from './LogBooks';
import CustomerEmail from './CustomerEmail';
import HeadingCp from '../components/HeadingCp';
import Accordion from '../components/Accordion';
import LoadingSpinner from '../components/LoadingSpinner';

function InspectionDetails() {
  const { id: urlID } = useParams();
  const navigate = useNavigate();

  const [inspection, setInspection] = useState({});
  const [activeInspection, setActiveInspection] = useState(true);
  const [type, setType] = useState('');
  const [deviceList, setDeviceList] = useState({});
  const [inspectionTech, setInspectionTech] = useState({ techNotes: '' });
  const [selectedTech, setSelectedTech] = useState([]);         // Always start as an array
  const [techList, setTechList] = useState([]);
  const [logbookstatus, setLogbookStatus] = useState('');
  const [inspection_report, setInspectionReport] = useState({});
  const [activateSignature, setActivateSignature] = useState(false);
  const [panelList, setPanelList] = useState([]);
  const [ready, setReady] = useState(false);
  const [showDeviceBox, setShowDeviceBox] = useState(false);
  const [showLogBookBox, setShowLogBookBox] = useState(false);
  const [showCustomerEmailBox, setShowCustomerEmailBox] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  const [isModalFileGenOpen, setIsModalFileGenOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  //below for folder creation and image upload
  const [filesToUpload, setFilesToUpload] = useState([]);  // an array of files
  const [folderId, setFolderId] = useState(null);          // store created folder ID
  const [selectedFiles, setSelectedFiles] = useState([]);  // an array of files
  
  const [file, setFile] = useState(null);
  const [selectGenFile, setSelectGenFile] = useState(null);

  const [isNotesOpen, setIsNotesOpen] = useState(false);

  const [userId, setUserId] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchPanelList(urlID);
    fetchInspectionData(urlID);
    fetchDeviceList(urlID);
    fetchTechList(urlID);
  }, [urlID, ready]);

  // 1) Panel list
  const fetchPanelList = async (urlID) => {
    try {
      const response = await axios.get(`${apiUrl}/contracts_crud.php?getPanel=${urlID}`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      if (response.data[0] === 'success') {
        setPanelList(response.data[1]);
      } else {
        console.error('Error getting panel list');
      }
    } catch (error) {
      console.error('Error fetching panel list:', error);
    }
  };

  // 2) Inspection Data
  const fetchInspectionData = async (urlID) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/contracts_crud.php?getInspectionDetails=${urlID}`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      if (response.data[0] === 'success') {
        const data = response.data[1];
        setInspection(data);
        setActiveInspection(true);
        setInspectionTech({ techNotes: data.tech_notes });
        setLogbookStatus(data.status);
        setUserId(response.data[3] || null); // assuming data[3] contains user info

        // If there's a customer_notification_email, prepare for signature
        if (data.customer_notification_email) {
          setActivateSignature(true);
          setInspectionReport({
            customerPrint: data.customer_print,
            customerEmail: data.customer_notification_email
          });
        }

        setType(data.type);
        // Show/hide sections
        if (data.type) {
          setShowDeviceBox(true);
          // Possibly use data[3].status checks if needed
          // setShowCustomerEmailBox(...) if logic requires
        } else {
          setShowDeviceBox(false);
          setShowCustomerEmailBox(false);
        }
      } else {
        console.error('Error getting inspection data');
      }
    } catch (error) {
      console.error('Error fetching inspection data:', error);

    } finally {
        setLoading(false);
    }
  };

  // 3) Device List
  const fetchDeviceList = async (urlID) => {
    try {
      const response = await axios.get(`${apiUrl}/contracts_crud.php?getDeviceList=${urlID}`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      const data = response.data;
      if (data[0] === 'success') {
        setReady(data[2]);
        setDeviceList(data[1]);

        const logbook = data[4];
        // const checkListMainMissing = data[6]; // if needed

        if (ready) {
          setShowLogBookBox(true);
          if (logbook?.status) {
            setLogbookStatus(logbook.status);
            setShowCustomerEmailBox(true);
          }
        } else {
          setShowLogBookBox(false);
          setShowCustomerEmailBox(false);
        }
      } else {
        console.error('Error getting device list');
      }
    } catch (error) {
      console.error('Error fetching device list:', error);
    }
  };

  // 4) Tech List
  const fetchTechList = async (urlID) => {
    try {
      const response = await axios.get(`${apiUrl}/contracts_crud.php?getAdditionalTech=${urlID}`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
      });
      if (response.data[0] === 'success') {
        setTechList(response.data[1]);
        
        // -- MAIN FIX: Ensure `selectedTech` is always an array --
        const possibleSelectedTech = response.data[2];
        if (Array.isArray(possibleSelectedTech)) {
          setSelectedTech(possibleSelectedTech);
        } else {
          // If it's not an array, fallback to empty array
          setSelectedTech([]);
          console.warn('selectedTech from server is not an array, defaulting to []');
        }
      } else {
        console.error('Error getting tech list');
      }
    } catch (error) {
      console.error('Error fetching tech list:', error);
    }
  };

  // Navigations
  const newInspection = (type) => {
    if (type === 'new') {
      navigate('/mdep_m_inspection/new');
    } else {
      navigate('/mdep_m_inspection/list');
    }
  };

  const signature = () => {
    navigate(`/t/tinspsignature/${urlID}`);
  };

  // Accordion toggles
  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleGenUploadFile = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a file before uploading.", { position: 'top-center' });
      return;
    }
    if(!inspection?.inspection_id){ 
      toast.error("Please select a file before uploading.", { position: 'top-center' });
      return 
    }
    const formData = new FormData();
    formData.append('status', 'submit_image');
    formData.append('inspection_id', inspection?.inspection_id);
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${apiUrl}/maintenance_inspection_image_upload.php`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.info('File uploaded successfully!', { position: 'top-center' });
        setInspection(prev => ({ ...prev, image_name: response.data[0] }));
        setFile(null);
        setIsModalFileGenOpen(false);
      } else {
        toast.error(response.data.error || "Error uploading file", { position: 'top-center' });
      }
    } catch (error) {
      toast.error("An error occurred during file upload.", { position: 'top-center' });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      toast.error('No file selected.', { position: 'top-center' });
      setFile(null);
      return;
    }

    const validFileTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes

    if (!validFileTypes.includes(selectedFile.type)) {
      toast.error('Invalid file type. Only PDF, PNG, and JPEG are allowed.', {
        position: 'top-center',
      });
      setFile(null);
      return;
    }

    if (selectedFile.size > maxSize) {
      toast.error('File size exceeds 2MB.', { position: 'top-center' });
      setFile(null);
      return;
    }

    setFile(selectedFile);
  };

  // Add photos
  const handleAddPhotosClick = async () => {
    if (!inspection?.job_site_address) {
      toast.error("No job site address found!", { position: 'top-center' });
      return;
    }

    try {
      // 1) Create folder
      const folderName = inspection.job_site_address;
      const response = await axios.post(`${apiUrl}/drive_folder.php`, {
        status: 'createFolder',
        inspection_id: inspection.inspection_id,
        data: { folderName }
      });
      if (response.data?.success) {
        const newFolderId = response.data.folderId;
        setFolderId(newFolderId);       // store the folder ID
        setIsModalFileGenOpen(true);    // open the modal to pick images
      } else {
        toast.error('Error creating folder: ' + response.data.error, { position: 'top-center' });
      }

    } catch (error) {
      console.error('Error creating folder', error);
      toast.error('Failed to create folder', { position: 'top-center' });
    }
  };

  // Multi-file upload
  const handleMultiFileUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!folderId) {
      toast.error("No folder ID. Please create folder first.", { position: 'top-center' });
      return;
    }
    if (!selectedFiles.length) {
      toast.error("No files selected.", { position: 'top-center' });
      return;
    }

    try {
      for (const file of selectedFiles) {
        // 1) Build a FormData for each file
        const formData = new FormData();
        formData.append('status', 'uploadFile');  // or your backend route
        formData.append('folderId', folderId);
        formData.append('file', file);

        // 2) Post to your backend
        const uploadRes = await axios.post(`${apiUrl}/drive_folder.php`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (!uploadRes.data?.success) {
          toast.error(`Error uploading ${file.name}: ${uploadRes.data?.error || 'Unknown error'}`, {
            position: 'top-center'
          });
        } else {
          // success => maybe you do something
        }
      }
      toast.success("All photos uploaded!", { position: 'top-center' });
      // Clear the files & close modal
      setFilesToUpload([]);
      setIsModalFileGenOpen(false);

    } catch (err) {
      console.error('Error uploading files', err);
      toast.error('Error uploading files', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    // do something with selectedFiles...
    setSelectedFiles(selectedFiles);
  };

  const openInMaps = () => {
  if (!inspection?.job_site_address) {
    toast.error("No job site address available", { position: 'top-center' });
    return;
  }
  const fullAddress = [
    inspection.job_site_address,
    inspection.job_site_address2,
    inspection.city,
    inspection.state,
    inspection.zip_code,
  ]
    .filter(Boolean)
    .join(' ');

  const encoded = encodeURIComponent(fullAddress);
  window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
};


  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header */}
      <div className="px-4 pt-4">
        <h1 className="text-xl font-semibold text-gray-900">Maintenance Inspection</h1>
        <p className="text-xs text-gray-500">Tech view</p>
      </div>

       {!loading && activeInspection && inspection.lead_tech_id === userId &&(
        <>
          {/* Top actions */}
          <div className="px-4 mt-3">
            <button
              type="button"
              onClick={handleAddPhotosClick}
              className="w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              Add Photos
            </button>
          </div>        
        </>
       )}
      {/* Loading */}
      {loading && (
        <div className="px-4 py-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 w-2/3 rounded bg-gray-200" />
            <div className="h-24 w-full rounded-2xl bg-gray-200" />
            <div className="h-40 w-full rounded-2xl bg-gray-200" />
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && activeInspection && (
        <div className="px-4 py-4 space-y-4">

          {/* Summary Card */}
          <div className="rounded-2xl bg-white/95 backdrop-blur shadow ring-1 ring-gray-200 p-4">
            {/* Chips row */}
            <div className="flex flex-wrap items-center gap-2">
              {inspection.system_type && (
                <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-medium text-indigo-700">
                  {inspection.system_type}
                </span>
              )}
              {inspection.status && (
                <span
                  className={[
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
                    String(inspection.status).toLowerCase().includes('complete')
                      ? 'bg-green-50 text-green-700 ring-green-600/20'
                      : String(inspection.status).toLowerCase().includes('tentative')
                      ? 'bg-red-50 text-red-700 ring-red-600/20'
                      : 'bg-amber-50 text-amber-700 ring-amber-600/20',
                  ].join(' ')}
                >
                  {inspection.inspection_status}
                </span>
              )}
              <span className="ml-auto text-xs font-semibold text-gray-500">
                #{inspection.inspection_id || urlID}
              </span>
            </div>

            {/* Customer / Site */}
            <div className="mt-3">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {inspection.customer_name || '—'}
              </div>
              <div className="mt-0.5 text-xs text-gray-600">
                {[inspection.job_site_address, inspection.job_site_address2].filter(Boolean).join(', ') || '—'}
              </div>
              <div className="text-xs text-gray-600">
                {[inspection.city, inspection.state, inspection.zip_code].filter(Boolean).join(' ') || ' '}
              </div>
            </div>

            {/* Dates */}
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Schedule</div>
                <div className="mt-0.5 font-medium text-gray-900">
                  {inspection.scheduled_due_date || '—'}
                  <span className="mx-1 text-gray-400">→</span>
                  {inspection.schedule_date_to || '—'}
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Contact</div>
                <div className="mt-0.5 font-medium text-gray-900">
                  {inspection.contact_name || '—'}
                </div>
                <div className="text-xs text-gray-600">{inspection.contact_phone || ' '}</div>
              </div>
            </div>

            {/* Additional Tech*/}
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Additional Tech</div>
              <div className="text-sm font-semibold text-gray-900 truncate">
                {inspection.additional_tech_first_name || '—'} {inspection.additional_tech_last_name || '—'}
              </div>
            </div>            
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsNotesOpen(true)}
              className="mt-1 w-1/2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              View Inspection Notes
            </button>

          <button
            type="button"
            onClick={openInMaps}
            className="mt-1 w-1/2 rounded-xl border border-gray-300 bg-gray-500 px-4 py-2.5 
                      text-sm font-semibold text-white shadow-sm
                    hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Open in Maps
          </button>            
          </div>


          {inspection.lead_tech_id !== userId ? (<p className="text-sm text-red-700">You are not authorized to view this section.</p>) : 
          (
            <>
          {/* Accordions */}
            <div className="space-y-3">
              <Accordion
                title="All Devices Tested and Operational (Required)"
                content={
                  <DevicesTested
                    deviceList={deviceList}
                    setDeviceList={setDeviceList}
                    urlID={urlID}
                    ready={ready}
                    setReady={setReady}
                    showLogBookBox={showLogBookBox}
                    setShowLogBookBox={setShowLogBookBox}
                    setShowCustomerEmailBox={setShowCustomerEmailBox}
                  />
                }
                isOpen={openIndex === 0}
                toggle={() => setOpenIndex(openIndex === 0 ? null : 0)}
              />

              <Accordion
                title="Tech Notes"
                content={
                  <TechNotes
                    inspectionTech={inspectionTech}
                    setInspectionTech={setInspectionTech}
                    urlID={urlID}
                  />
                }
                isOpen={openIndex === 1}
                toggle={() => setOpenIndex(openIndex === 1 ? null : 1)}
              />

              <Accordion
                title="Additional Tech"
                content={
                  <AdditionalTech
                    selectedTech={selectedTech}
                    setSelectedTech={setSelectedTech}
                    techList={techList}
                    urlID={urlID}
                  />
                }
                isOpen={openIndex === 2}
                toggle={() => setOpenIndex(openIndex === 2 ? null : 2)}
              />

              <Accordion
                title="Log Books (Required)"
                content={
                  !showLogBookBox
                    ? <span className="text-sm text-red-700">You need to verify all devices first.</span>
                    : (
                      <LogBooks
                        logbookstatus={logbookstatus}
                        setLogbookStatus={setLogbookStatus}
                        urlID={urlID}
                        setShowCustomerEmailBox={setShowCustomerEmailBox}
                      />
                    )
                }
                isOpen={openIndex === 3}
                toggle={() => setOpenIndex(openIndex === 3 ? null : 3)}
              />

              <Accordion
                title="Ticket Notification (Required)"
                content={
                  showCustomerEmailBox && (
                    <CustomerEmail
                      inspection_report={inspection_report}
                      setInspectionReport={setInspectionReport}
                      activateSignature={activateSignature}
                      setActivateSignature={setActivateSignature}
                      urlID={urlID}
                      signature={signature}
                    />
                  )
                }
                isOpen={openIndex === 4}
                toggle={() => setOpenIndex(openIndex === 4 ? null : 4)}
              />
            </div>
            </>            
          )}
        </div>
      )}

      {/* Sticky footer actions */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto w-full max-w-md px-4 py-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={()=> navigate('/t/tinspection')}
              className="w-1/2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Inspection Queue
            </button>

            <button
              type="button"
              onClick={()=> navigate('/t/tech_schedule')}
              className="w-1/2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Tech Schedules
            </button>
          </div>
        </div>
      </div>

      {/* Photo Upload Bottom Sheet */}
      {isModalFileGenOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalFileGenOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md rounded-t-2xl bg-white/95 backdrop-blur-md shadow-2xl ring-1 ring-gray-200">
            <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-gray-300" />
            <div className="px-4 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Upload Photos for {inspection?.job_site_address || 'Job Site'}
              </h2>

              <form onSubmit={handleMultiFileUpload} className="mt-3 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Select images (Max 2MB each)
                  </label>
                  <input
                    type="file"
                    accept="image/png, image/jpeg"
                    multiple
                    onChange={handleFilesChange}
                    className="mt-2 block w-full text-sm"
                  />
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setIsModalFileGenOpen(false); setFilesToUpload([]); }}
                    className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {loading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Inspection Notes Bottom Sheet */}
      {isNotesOpen && (
        <div className="fixed inset-0 z-50">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsNotesOpen(false)}
          />
          {/* Bottom sheet panel */}
          <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md rounded-t-2xl bg-white/95 backdrop-blur-md shadow-2xl ring-1 ring-gray-200">
            {/* Drag handle */}
            <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-gray-300" />
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Inspection Notes</h3>
              <button
                type="button"
                onClick={() => setIsNotesOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Close
              </button>
            </div>
            {/* Body */}
            <div className="px-4 py-4 max-h-[65vh] overflow-y-auto">
              <div className="whitespace-pre-wrap text-sm text-gray-900">
                {inspection?.inspection_notes && inspection.inspection_notes.trim().length > 0
                  ? inspection.inspection_notes
                  : '— No inspection notes —'}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );

}

export default InspectionDetails;
